export const AI_MODEL_TYPES = ['CHAT', 'IMAGE', 'VIDEO', 'AVATAR', 'AUDIO', 'MUSIC', 'EMBED'] as const;

export type AiModelType = (typeof AI_MODEL_TYPES)[number];

export const AI_MODEL_TYPE_LABELS: Record<AiModelType, string> = {
  CHAT: '对话',
  IMAGE: '生图',
  VIDEO: '视频',
  AVATAR: '数字人',
  AUDIO: '语音',
  MUSIC: '音乐',
  EMBED: '向量',
};

/** 编辑时「未修改密钥」占位符，与后端 AI_PROVIDER_SECRET_PLACEHOLDER 一致 */
export const AI_PROVIDER_SECRET_PLACEHOLDER = '********';

export const AI_BILLING_CURRENCIES = ['USD', 'CNY'] as const;
export type AiBillingCurrency = (typeof AI_BILLING_CURRENCIES)[number];
export const AI_BILLING_CURRENCY_LABELS: Record<AiBillingCurrency, string> = {
  USD: '美元',
  CNY: '人民币',
};

export const AI_BILLING_DIMENSIONS = [
  'INPUT_TOKEN',
  'OUTPUT_TOKEN',
  'CACHE_TOKEN',
  'IMAGE',
  'VIDEO_SECOND',
  'AUDIO_MINUTE',
  'CHARACTER',
  'VOICE_CLONE',
  'SONG',
  'GPU_SECOND',
] as const;
export type AiBillingDimension = (typeof AI_BILLING_DIMENSIONS)[number];
export const AI_BILLING_DIMENSION_LABELS: Record<AiBillingDimension, string> = {
  INPUT_TOKEN: '输入 Token',
  OUTPUT_TOKEN: '输出 Token',
  CACHE_TOKEN: '缓存命中 Token',
  IMAGE: '图片',
  VIDEO_SECOND: '视频秒数',
  AUDIO_MINUTE: '音频分钟',
  CHARACTER: '字符数',
  VOICE_CLONE: '声音克隆',
  SONG: '歌曲',
  GPU_SECOND: 'GPU 秒',
};

export const AI_BILLING_UNITS = [
  '1M_TOKEN',
  'IMAGE',
  'SECOND',
  'MINUTE',
  '1K_CHARACTER',
  'VOICE',
  'SONG',
] as const;
export type AiBillingUnit = (typeof AI_BILLING_UNITS)[number];
export const AI_BILLING_UNIT_LABELS: Record<AiBillingUnit, string> = {
  '1M_TOKEN': '百万 Token',
  IMAGE: '张',
  SECOND: '秒',
  MINUTE: '分钟',
  '1K_CHARACTER': '千字符',
  VOICE: '音色',
  SONG: '首',
};

/** usage → 允许的 unit 列表 */
export const AI_DIMENSION_UNIT_MAP: Record<AiBillingDimension, readonly AiBillingUnit[]> = {
  INPUT_TOKEN: ['1M_TOKEN'],
  OUTPUT_TOKEN: ['1M_TOKEN'],
  CACHE_TOKEN: ['1M_TOKEN'],
  IMAGE: ['IMAGE'],
  VIDEO_SECOND: ['SECOND'],
  AUDIO_MINUTE: ['MINUTE'],
  CHARACTER: ['1K_CHARACTER'],
  VOICE_CLONE: ['VOICE'],
  SONG: ['SONG'],
  GPU_SECOND: ['SECOND'],
};

export const AI_BILLING_RESOLUTIONS = ['720P', '1080P', '4K'] as const;
export type AiBillingResolution = (typeof AI_BILLING_RESOLUTIONS)[number];

/** rule.inputParameters / invoke 入参分档键（见 AI计费与统计规则.md §1.0.3） */
export const AI_BILLING_INPUT_PARAMETER_LABELS = {
  hasVideoInput: '是否有参考视频',
  hasAudioOutput: '输出是否有声',
  resolution: '输出分辨率',
} as const;

export type AiBillingInputParameterKey = keyof typeof AI_BILLING_INPUT_PARAMETER_LABELS;

export type BillingRuleInputParameters = Partial<
  Record<'hasVideoInput' | 'hasAudioOutput', boolean> & { resolution: AiBillingResolution }
>;

export function normalizeBillingRuleInputParameters(
  raw: unknown
): BillingRuleInputParameters | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const row = raw as Record<string, unknown>;
  const result: BillingRuleInputParameters = {};
  if ('hasVideoInput' in row) result.hasVideoInput = Boolean(row.hasVideoInput);
  if ('hasAudioOutput' in row) result.hasAudioOutput = Boolean(row.hasAudioOutput);
  if (
    typeof row.resolution === 'string' &&
    (AI_BILLING_RESOLUTIONS as readonly string[]).includes(row.resolution)
  ) {
    result.resolution = row.resolution as AiBillingResolution;
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

export function isBillingRuleInputParametersConfigured(
  params?: BillingRuleInputParameters | null
): boolean {
  return !!params && Object.keys(params).length > 0;
}

export function formatBillingRuleInputParametersSummary(
  params?: BillingRuleInputParameters | null
): string {
  if (!isBillingRuleInputParametersConfigured(params)) return '无配置';
  const parts: string[] = [];
  if (params?.hasVideoInput !== undefined) {
    parts.push(`${AI_BILLING_INPUT_PARAMETER_LABELS.hasVideoInput}:${params.hasVideoInput ? '有' : '无'}`);
  }
  if (params?.hasAudioOutput !== undefined) {
    parts.push(`${AI_BILLING_INPUT_PARAMETER_LABELS.hasAudioOutput}:${params.hasAudioOutput ? '有' : '无'}`);
  }
  if (params?.resolution) {
    parts.push(`${AI_BILLING_INPUT_PARAMETER_LABELS.resolution}:${params.resolution}`);
  }
  return parts.join(' · ') || '已配置';
}

export const AI_INVOKE_STATUSES = ['SUCCESS', 'FAILED', 'REFUNDED'] as const;
export type AiInvokeStatus = (typeof AI_INVOKE_STATUSES)[number];
export const AI_INVOKE_STATUS_LABELS: Record<AiInvokeStatus, string> = {
  SUCCESS: '成功',
  FAILED: '失败',
  REFUNDED: '已退款',
};

/** datetime-local 控件用：Date / ISO → yyyy-MM-ddTHH:mm:ss */
export function toDatetimeLocalValue(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** datetime-local 控件值 → ISO 8601 */
export function datetimeLocalToIso(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}
